// LT-314 — First-run onboarding checklist panel. Renders on the Dashboard
// for fresh users. Dismissable; dismissal persists across reloads
// (localStorage). Tasks link into the product and auto-tick when the
// corresponding action is taken elsewhere.
//
// Analytics:
//   - `onboarding.checklist.viewed` — fired once per mount (per user) when
//     the panel becomes visible.
//   - `onboarding.checklist.task_completed` — fired when a task
//     transitions completed=false → completed=true (auto or manual).
//   - `onboarding.checklist.dismissed` — fired on explicit dismiss click.
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Rocket, X } from 'lucide-react';

import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import { IconButton } from '../../ui';
import {
  type AutoCompleteSignals,
  type ChecklistTaskId,
  useOnboardingChecklist,
} from './useOnboardingChecklist';

interface OnboardingChecklistProps {
  userId: string | null | undefined;
  signals: AutoCompleteSignals;
}

const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({ userId, signals }) => {
  const { tasks, visible, completeCount, dismiss } = useOnboardingChecklist(userId, signals);

  // Fire `viewed` once per user per session when the panel becomes visible.
  // Guard with a ref so re-renders (task-state updates, etc.) don't spam the
  // event bus.
  const viewedFiredRef = useRef(false);
  useEffect(() => {
    if (visible && !viewedFiredRef.current) {
      track(AnalyticsEvents.ONBOARDING_CHECKLIST_VIEWED, {
        total: tasks.length,
        already_complete: completeCount,
      });
      viewedFiredRef.current = true;
    }
  }, [visible, tasks.length, completeCount]);

  // Fire `task_completed` on false → true transitions for any task.
  const prevCompletedRef = useRef<Set<ChecklistTaskId>>(new Set());
  useEffect(() => {
    const prev = prevCompletedRef.current;
    const currentlyCompleted = new Set<ChecklistTaskId>();
    tasks.forEach((t) => {
      if (t.completed) currentlyCompleted.add(t.id);
    });
    // New transitions = currently - previous.
    currentlyCompleted.forEach((id) => {
      if (!prev.has(id)) {
        track(AnalyticsEvents.ONBOARDING_CHECKLIST_TASK_COMPLETED, { task: id });
      }
    });
    prevCompletedRef.current = currentlyCompleted;
  }, [tasks]);

  if (!visible) return null;

  const handleDismiss = () => {
    track(AnalyticsEvents.ONBOARDING_CHECKLIST_DISMISSED, {
      completed: completeCount,
      total: tasks.length,
    });
    dismiss();
  };

  return (
    <section
      data-testid="onboarding-checklist"
      aria-labelledby="onboarding-checklist-title"
      className="bg-surface-1 border border-white/[0.08] rounded-ds-xl p-4 mb-4 relative"
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-ds-lg bg-accent-tint border border-accent-border shrink-0">
          <Rocket className="w-4 h-4 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <h2 id="onboarding-checklist-title" className="text-sm font-semibold text-slate-50">
              Get set up in {tasks.length} steps
            </h2>
            <span className="text-xs text-slate-400">
              {completeCount}/{tasks.length} done
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            A quick tour of the essentials — tick them off as you go.
          </p>
        </div>
        <IconButton
          intent="ghost"
          size="sm"
          aria-label="Dismiss onboarding checklist"
          onClick={handleDismiss}
        >
          <X className="w-4 h-4" />
        </IconButton>
      </div>

      <ul className="mt-3 space-y-1">
        {tasks.map((task) => (
          <li key={task.id}>
            <Link
              to={task.to}
              className={`flex items-start gap-3 rounded-ds-md px-3 py-2 transition-colors ${
                task.completed
                  ? 'opacity-60 hover:bg-surface-2'
                  : 'hover:bg-surface-2'
              }`}
              data-task-id={task.id}
              data-task-completed={task.completed || undefined}
            >
              {task.completed ? (
                <CheckCircle2 className="w-4 h-4 text-ok mt-0.5 shrink-0" aria-hidden />
              ) : (
                <Circle className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" aria-hidden />
              )}
              <div className="min-w-0">
                <div
                  className={`text-sm font-medium ${
                    task.completed ? 'text-slate-400 line-through' : 'text-slate-50'
                  }`}
                >
                  {task.label}
                </div>
                <div className="text-xs text-slate-400">{task.description}</div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default OnboardingChecklist;
